import React, { useState, useRef, useMemo } from 'react';
import DashboardTable from './DashboardTable';
import IconButton from './IconButton';
import Pagination from '../../../components/UI/TableGrid/Pagination';
import EmrVisitsService from '../../../services/EmrVisitsService';
import FileManagerService from '../../../services/FileManagerService';

const WorkRestrictions = ({ data = [], onActionClick, isLoading = false, onExpand, Loader, Toast }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [initialPosition, setInitialPosition] = useState(null);
  const containerRef = useRef(null);
  const originalPositionRef = useRef(null);
  const [allData, setAllData] = useState([]); // Store full data when in fullscreen
  const [tableData, setTableData] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 10,
    totalRecords: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [patientName, setPatientName] = useState('');
  const [sortColumn, setSortColumn] = useState('visit_date');
  const [sortDirection, setSortDirection] = useState('desc');

  // Only show Work Comp visits (Work Restrictions are relevant to work comp only)
  const filteredData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data.filter((row) => {
      const category = row.visit_category ?? row.type ?? '';
      return typeof category === 'string' && category.startsWith('Work Comp');
    });
  }, [data]);

  // Update data when component receives new data
  React.useEffect(() => {
    if (isFullscreen) {
      // When fullscreen, use the filtered data (work comp only)
      setAllData(filteredData);
      setPagination(prev => ({ ...prev, totalRecords: filteredData.length, page: 1 }));
      const start = 0;
      const end = pagination.perPage;
      setTableData(filteredData.slice(start, end));
    } else {
      // When not fullscreen, use incoming filtered data from parent (API-sorted)
      setTableData(filteredData);
      setAllData([]);
      setPagination({ page: 1, perPage: 10, totalRecords: 0 });
      setSearchTerm('');
      setPatientName('');
      // Don't reset sortColumn/sortDirection - preserve user's sort selection
    }
  }, [filteredData, isFullscreen]);

  // Apply pagination when in fullscreen (sort and search are API-based)
  React.useEffect(() => {
    if (isFullscreen && allData.length > 0) {
      const totalRecords = allData.length;
      const maxPage = Math.max(1, Math.ceil(totalRecords / pagination.perPage));
      const currentPage = pagination.page > maxPage ? 1 : pagination.page;
      setPagination(prev => ({ ...prev, totalRecords, page: currentPage }));
      const start = (currentPage - 1) * pagination.perPage;
      const end = start + pagination.perPage;
      setTableData(allData.slice(start, end));
    }
  }, [allData, isFullscreen, pagination.page, pagination.perPage]);

  const handlePaginationChange = (newPagination) => {
    setPagination(newPagination);
  };

  const handleSort = (column, direction) => {
    setSortColumn(column);
    setSortDirection(direction);
    if (onExpand) {
      setPagination(prev => ({ ...prev, page: 1 }));
      onExpand(isFullscreen, patientName, column, direction);
    }
  };

  

  const SettingsIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M9.4155 6.58674C10.1962 7.36741 10.1962 8.63474 9.4155 9.41674C8.63483 10.1974 7.3675 10.1974 6.5855 9.41674C5.80483 8.63608 5.80483 7.36874 6.5855 6.58674C7.3675 5.80474 8.63417 5.80474 9.4155 6.58674"
        stroke="#1D1917"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M2 7.99992C2 7.56059 2.10133 7.12592 2.29733 6.72525V6.72525C3.30733 4.66059 5.53933 3.33325 8 3.33325C10.4607 3.33325 12.6927 4.66059 13.7027 6.72525V6.72525C13.8987 7.12592 14 7.56059 14 7.99992C14 8.43925 13.8987 8.87392 13.7027 9.27458V9.27458C12.6927 11.3393 10.4607 12.6666 8 12.6666C5.53933 12.6666 3.30733 11.3393 2.29733 9.27458V9.27458C2.10133 8.87392 2 8.43925 2 7.99992Z"
        stroke="#1D1917"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const CloseIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12 4L4 12M4 4L12 12"
        stroke="#1D1917"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  // Handle action click (view button)
  const handleActionClick = (row, action) => {
    if (action === 'view') {
      const visitId = row.visit_id;
      if (visitId) {
        handleDownloadWorkStatusForm(visitId);
      } else {
        if (Toast && typeof Toast.error === 'function') {
          Toast.error("Visit ID not found");
        }
      }
    } else {
      // Fallback to parent's onActionClick if provided
      if (onActionClick) {
        onActionClick(row, action);
      }
    }
  };

  // Download work status form handler
  const handleDownloadWorkStatusForm = async (visitId) => {
    if (!visitId) return;
    
    if (Loader && typeof Loader === 'function') {
      Loader(true);
    }
    
    try {
      const response = await EmrVisitsService.printWorkStatusForm(visitId);
      
      // Handle different response structures
      const base64Data = typeof response === 'string' ? response : (response?.data || response);
      
      // Check if data is empty
      const isEmpty = !base64Data || 
                     base64Data === '' || 
                     base64Data === null || 
                     base64Data === undefined ||
                     (typeof base64Data === 'string' && base64Data.trim().length === 0) ||
                     (typeof base64Data === 'object' && Object.keys(base64Data).length === 0);
      
      if (isEmpty) {
        console.log("Work status form data is empty:", base64Data);
        if (Toast && typeof Toast.error === 'function') {
          Toast.error("Work status form is not available");
        } else {
          console.warn("Toast is not available or Toast.error is not a function", Toast);
        }
        if (Loader && typeof Loader === 'function') {
          Loader(false);
        }
        return;
      }
      
      const pdfData = {
        file_content: base64Data,
        original_name: "work-status-report",
        mime_type: "application/pdf"
      };
      
      FileManagerService.printPdf(pdfData);
      if (Loader && typeof Loader === 'function') {
        Loader(false);
      }
    } catch (error) {
      console.error("Error downloading work status form:", error);
      if (Toast && typeof Toast.error === 'function') {
        Toast.error("Work status form is not available");
      } else {
        console.warn("Toast is not available or Toast.error is not a function", Toast);
      }
      if (Loader && typeof Loader === 'function') {
        Loader(false);
      }
    }
  };

  const handleToggleFullscreen = () => {
    if (!isFullscreen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const pos = {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height
      };
      originalPositionRef.current = pos;
      setInitialPosition(pos);
      setIsFullscreen(true);
      setTimeout(() => {
        setInitialPosition(null);
      }, 50);
      if (onExpand) {
        onExpand(true, '', sortColumn, sortDirection);
      }
    } else {
      if (originalPositionRef.current) {
        setInitialPosition(originalPositionRef.current);
        setTimeout(() => {
                        setIsFullscreen(false);
                        setInitialPosition(null);
                        originalPositionRef.current = null;
                        setPatientName(''); // Clear patient name when closing
                        if (onExpand) {
                          onExpand(false, '', sortColumn, sortDirection);
                        }
        }, 300);
      } else {
        setIsFullscreen(false);
        setInitialPosition(null);
        setPatientName('');
        if (onExpand) {
          onExpand(false, '', sortColumn, sortDirection);
        }
      }
    }
  };

  return (
    <div
      ref={containerRef}
      className={`${isFullscreen ? 'tblviewcard flip fullscreen-card' : 'col-lg-6 mb-2 pb-1'}`}
      style={isFullscreen ? {
        position: 'fixed',
        top: initialPosition ? `${initialPosition.top}px` : '0',
        left: initialPosition ? `${initialPosition.left}px` : '0',
        transform: initialPosition ? 'none' : 'none',
        width: initialPosition ? `${initialPosition.width}px` : '100vw',
        height: initialPosition ? `${initialPosition.height}px` : '100vh',
        zIndex: 1040,
        backgroundColor: '#fff',
        padding: initialPosition ? '20px' : '20px',
        borderRadius: initialPosition ? '8px' : '0',
        boxShadow: initialPosition ? '0 4px 20px rgba(0,0,0,0.3)' : 'none',
        transition: 'all 0.3s ease-in-out'
      } : { transition: 'all 0.3s ease-in-out' }}
    >
      <div className="card mb-0 border-0 shadow-none" style={isFullscreen ? { height: '100%', margin: 0 } : {}}>
        <div className="card-header sh_accordionheader">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="text-md font-weight-bold mb-0">Work Restrictions</h6>
            <div>
              <a
                className="btn sh_btniconnew"
                style={{ cursor: 'pointer' }}
                onClick={handleToggleFullscreen}
                title={isFullscreen ? 'Minimize' : 'Maximize'}
              >
                {isFullscreen ? <CloseIcon /> : <SettingsIcon />}
              </a>
            </div>
          </div>
        </div>
        <div className={`card-body ${isFullscreen ? '' : 'sh_dashtableoverflowcard'} px-0 pt-0 mt-2 pb-2`} style={isFullscreen ? { height: 'calc(100% - 60px)', overflow: 'auto', display: 'flex', flexDirection: 'column' } : {}}>
          {isLoading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
              {/* <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div> */}
            </div>
          ) : (
            <>
               {isFullscreen && (
                <div className="px-3 pt-2 pb-2 d-flex align-items-center justify-content-end" style={{ borderBottom: '1px solid #e0e0e0' }}>
                  <div className="d-flex align-items-center">
                    <div className="mr-2">
                      <input
                        type="text"
                        placeholder="Employee"
                        value={patientName}
                        onChange={(e) => {
                          setPatientName(e.target.value);
                        }}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            const trimmed = patientName.trim();
                            setPatientName(trimmed);
                            setPagination(prev => ({ ...prev, page: 1 }));
                            if (onExpand) {
                              onExpand(true, trimmed, sortColumn, sortDirection);
                            }
                          }
                        }}
                        className="form-control"
                        style={{ minWidth: '200px' }}
                      />
                    </div>
                    <div className="addAction">
                      <div className="btn-group">
                        <button
                          className="btn btn-primary"
                          onClick={() => {
                            const trimmed = patientName.trim();
                            setPatientName(trimmed);
                            setPagination(prev => ({ ...prev, page: 1 }));
                            if (onExpand) {
                              onExpand(true, trimmed, sortColumn, sortDirection);
                            }
                          }}
                        >
                          Search
                        </button>
                        <button
                          className="btn btn-outline-secondary"
                          onClick={() => {
                            setPatientName('');
                            setPagination(prev => ({ ...prev, page: 1 }));
                            if (onExpand) {
                              onExpand(true, '', sortColumn, sortDirection);
                            }
                          }}
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div style={isFullscreen ? { flex: 1, overflow: 'auto' } : {}}>
                <DashboardTable
                  columns={['EMPLOYEE', 'TYPE', 'VISIT DATE', 'STATUS', 'ACTION']}
                  data={isFullscreen ? tableData : filteredData}
                  onActionClick={handleActionClick}
                  sortable={true}
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  emptyMessage="No work restrictions exists"
                  actionIcons={[
                  {
                    svg: (
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M5.83203 6.66667V3.33333C5.83203 2.87333 6.20536 2.5 6.66536 2.5H13.332C13.792 2.5 14.1654 2.87333 14.1654 3.33333V6.66667" stroke="#1D1917" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M5.83333 14.1667H4.16667C3.24583 14.1667 2.5 13.4209 2.5 12.5001V8.33341C2.5 7.41258 3.24583 6.66675 4.16667 6.66675H15.8333C16.7542 6.66675 17.5 7.41258 17.5 8.33341V12.5001C17.5 13.4209 16.7542 14.1667 15.8333 14.1667H14.1667" stroke="#1D1917" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path fillRule="evenodd" clipRule="evenodd" d="M5.83203 11.5H14.1654V16.6667C14.1654 17.1267 13.792 17.5 13.332 17.5H6.66536C6.20536 17.5 5.83203 17.1267 5.83203 16.6667V11.5Z" stroke="#1D1917" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M5.83203 9.16667H6.66536" stroke="#1D1917" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ),
                    title: 'Detail view',
                    action: 'view'
                  }
                ]}
                />
              </div>
              {isFullscreen && allData.length > 0 && pagination.totalRecords > pagination.perPage && (
                <div className="px-3 pb-2" style={{ borderTop: '1px solid #e0e0e0' }}>
                  <Pagination
                    pagination={pagination}
                    fetchTableRecords={handlePaginationChange}
                    tableRows={{ data: tableData }}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkRestrictions;

